// ActionButtons.jsx
// Component for purchase, message, and schedule meeting buttons - Modernized

import React from 'react';
import PurchaseButton from '../../components/PurchaseButton';
import ScheduleMeetButton from '../../components/ScheduleMeetButton';
import { COLORS, LAYOUT, BUTTON_STYLES } from '../../constants';

// Modern SVG Icons
const MessageCircleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const UserCheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <polyline points="16,11 18,13 22,9"></polyline>
  </svg>
);

const ActionButtons = ({ listing, seller, onMessageSeller, isOwner }) => {
  // Show owner message if user owns this listing
  if (isOwner) {
    return (
      <div style={modernStyles.ownerSection}>
        <div style={modernStyles.ownerCard}>
          <div style={modernStyles.ownerIcon}>
            <UserCheckIcon />
          </div>
          <div style={modernStyles.ownerContent}>
            <h3 style={modernStyles.ownerTitle}>Your Listing</h3>
            <p style={modernStyles.ownerDescription}>
              This is your listing. You can manage it from your dashboard or "My Items" page.
            </p>
            <button style={modernStyles.editButton}>
              <EditIcon />
              <span>Edit Listing</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={modernStyles.actionsContainer}>
      <div style={modernStyles.actionsHeader}>
        <h3 style={modernStyles.actionsTitle}>Interested in this item?</h3>
        <p style={modernStyles.actionsSubtitle}>
          Purchase securely or get in touch with the seller
        </p>
      </div>
      
      <div style={modernStyles.actionButtons}>
        {/* Primary Action - Purchase */}
        <div style={modernStyles.primaryAction}>
          <PurchaseButton listing={listing} />
        </div>
        
        {/* Secondary Actions */}
        <div style={modernStyles.secondaryActions}>
          {/* Message Seller */}
          <button 
            onClick={onMessageSeller} 
            style={{
              ...modernStyles.messageButton,
              ...(isOwner ? { opacity: 0.5, cursor: 'not-allowed' } : {})
            }}
            disabled={isOwner}
            title={isOwner ? "You can't message yourself." : "Message Seller"}
            onMouseEnter={(e) => {
              if (!isOwner) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isOwner) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            <MessageCircleIcon />
            <span>Message Seller</span>
          </button>
          
          {/* Schedule Meeting */}
          <ScheduleMeetButton
            recipientId={listing?.sellerId || listing?.createdBy || seller?.id}
            recipientName={seller?.name}
            style={modernStyles.scheduleButton}
            sellerId={listing?.sellerId || listing?.createdBy}
            listingId={listing?.id}
          />
        </div>
      </div>
      
      {/* Safety Notice */}
      <div style={modernStyles.safetyNotice}>
        <p style={modernStyles.safetyText}>
          <strong>Safety First:</strong> Meet in public places for safety. Redbird Markets facilitates secure transactions between ISU students.
        </p>
      </div>
    </div>
  );
};

// Modern styles
const modernStyles = {
  // Owner Section
  ownerSection: {
    marginTop: LAYOUT.spacing.xl,
  },
  ownerCard: {
    backgroundColor: `rgba(${COLORS.primary.replace('#', '').match(/.{2}/g).map(hex => parseInt(hex, 16)).join(', ')}, 0.05)`,
    border: `2px solid rgba(${COLORS.primary.replace('#', '').match(/.{2}/g).map(hex => parseInt(hex, 16)).join(', ')}, 0.15)`,
    borderRadius: LAYOUT.borderRadius.xl,
    padding: LAYOUT.spacing.xl,
    display: 'flex',
    alignItems: 'flex-start',
    gap: LAYOUT.spacing.lg,
  },
  ownerIcon: {
    width: '48px',
    height: '48px',
    backgroundColor: COLORS.primary,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.white,
    flexShrink: 0,
  },
  ownerContent: {
    flex: 1,
  },
  ownerTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: COLORS.primary,
    marginBottom: LAYOUT.spacing.xs,
  },
  ownerDescription: {
    fontSize: '0.875rem',
    color: COLORS.text.secondary,
    lineHeight: 1.5,
    marginBottom: LAYOUT.spacing.lg,
  },
  editButton: {
    ...BUTTON_STYLES.outlined,
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
    fontSize: '0.875rem',
    padding: `${LAYOUT.spacing.sm} ${LAYOUT.spacing.lg}`,
  },
  
  // Actions Container
  actionsContainer: {
    marginTop: LAYOUT.spacing.xl,
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.xl,
  },
  
  // Actions Header
  actionsHeader: {
    textAlign: 'center',
    paddingBottom: LAYOUT.spacing.lg,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  actionsTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: COLORS.text.primary,
    marginBottom: LAYOUT.spacing.xs,
  },
  actionsSubtitle: {
    fontSize: '0.875rem',
    color: COLORS.text.secondary,
    lineHeight: 1.5,
  },
  
  // Action Buttons
  actionButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: LAYOUT.spacing.lg,
  },
  primaryAction: {
    display: 'flex',
    justifyContent: 'center',
  },
  secondaryActions: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: LAYOUT.spacing.md,
  },
  messageButton: {
    ...BUTTON_STYLES.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: LAYOUT.spacing.sm,
    padding: `${LAYOUT.spacing.md} ${LAYOUT.spacing.lg}`,
    fontSize: '0.875rem',
    fontWeight: '600',
    transition: 'all 0.2s ease',
    transform: 'translateY(0)',
  },
  scheduleButton: {
    fontSize: '0.875rem',
    transition: 'all 0.2s ease',
    transform: 'translateY(0)',
  },
  
  // Safety Notice
  safetyNotice: {
    backgroundColor: COLORS.grayLight,
    borderRadius: LAYOUT.borderRadius.lg,
    padding: LAYOUT.spacing.md,
    borderLeft: `4px solid ${COLORS.primary}`,
  },
  safetyText: {
    fontSize: '0.75rem',
    color: COLORS.text.secondary,
    lineHeight: 1.4,
    margin: 0,
  },
};

export default ActionButtons;